/* ============================================================
   Личный кабинет в стиле новой витрины: шапка игрока, команда
   (состав, код, заявки), игровой профиль и статус заявки.

   Функциональность 1:1 со старой страницей (../../pages/Profile),
   которая остаётся для витрин 'cs' / 'fifa'. Строки берём из
   общего i18n (t('profile.*')) — он переключается вместе с языком
   витрины через i18n.changeLanguage() в fed/store.js.
   ============================================================ */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { Rv, Field, Pill } from '../ui';
import PremierBadge, { premierTier } from '../../cs/PremierBadge';

const ROLE_OPTS = [['igl', 'IGL'], ['awp', 'AWP'], ['entry', 'Entry'], ['support', 'Support'], ['lurk', 'Lurk']];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTS);

/** Статусы заявки → цвета витрины (как на странице турнира) */
const STATUS_TONE = {
  verified: { c: 'var(--fed-ok)', bg: 'rgba(67,217,106,.1)', bd: 'rgba(67,217,106,.4)' },
  rejected: { c: 'var(--fed-accent-bright)', bg: 'rgba(225,18,23,.08)', bd: 'var(--fed-line-accent)' },
  none: { c: 'var(--fed-fg-muted)', bg: 'var(--fed-surface-2)', bd: 'var(--fed-line)' },
  pending: { c: 'var(--fed-gold)', bg: 'rgba(230,183,60,.1)', bd: 'rgba(230,183,60,.35)' },
};
const toneOf = (status) => STATUS_TONE[status] || STATUS_TONE.pending;

function initials(name) {
  return (name || 'PL').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/** Спиннер витрины (fedSpin из fed.css) */
function Spin({ size = 18 }) {
  return (
    <span
      className="inline-block flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: '50%',
        border: '2px solid rgba(255,255,255,.15)', borderTopColor: 'var(--fed-accent-bright)',
        animation: 'fedSpin .7s linear infinite',
      }}
    />
  );
}

/** Карточка-панель витрины */
function Card({ children, className = '', style }) {
  return (
    <div className={`rounded-2xl bg-surface border border-line ${className}`} style={{ padding: 26, ...style }}>
      {children}
    </div>
  );
}

/** Моно-заголовок блока внутри карточки */
function BlockLabel({ children, color = 'var(--fed-fg-muted)', style }) {
  return (
    <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '.2em', color, ...style }}>
      {children}
    </div>
  );
}

/** Квадратный аватар-плашка с инициалами/тегом */
function Ava({ text, size = 40, radius = 11, accent = false, glowColor }) {
  return (
    <div
      className="flex-shrink-0 flex items-center justify-center font-display font-bold text-fg"
      style={{
        width: size, height: size, borderRadius: radius, fontSize: Math.round(size / 2.9),
        background: accent
          ? 'linear-gradient(140deg,var(--fed-accent-deep),var(--fed-surface-3))'
          : 'linear-gradient(150deg,var(--fed-surface-3),var(--fed-elevated))',
        border: `1px solid ${accent ? 'var(--fed-line-accent)' : 'var(--fed-line)'}`,
        boxShadow: glowColor ? `0 0 26px -8px ${glowColor}` : undefined,
      }}
    >
      {text}
    </div>
  );
}

function MetaTile({ l, v, color = 'var(--fed-fg)' }) {
  return (
    <div className="rounded-xl border border-line" style={{ padding: '13px 15px', background: 'rgba(10,10,12,.5)' }}>
      <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.12em' }}>{l}</div>
      <div className="font-semibold" style={{ fontSize: 14.5, marginTop: 5, color }}>{v}</div>
    </div>
  );
}

/** Модалка команды: код / список набора / создание.
    createOnly — только вкладка «Создать» (для профиля, где поиск команды убран). */
function TeamJoinModal({ open, onClose, onJoin, initialTab = 'code', createOnly = false }) {
  const { joinTeamByCode, createTeam, user } = useAppStore();
  const { t } = useTranslation();
  const canCreate = user?.status === 'verified' || user?.user_type === 'admin';
  const [tab, setTab] = useState(createOnly ? 'create' : initialTab);
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newRole, setNewRole] = useState('igl');
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (open) setTab(createOnly ? 'create' : initialTab); }, [open, initialTab, createOnly]);

  useEffect(() => {
    if (tab === 'browse' && teams.length === 0 && !loadingTeams) loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const data = await useAppStore.getState().fetchTeams('recruiting');
      setTeams(data || []);
    } catch (e) {
      console.error('Ошибка загрузки команд:', e);
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const join = async (teamCode, teamName) => {
    setJoining(teamCode);
    try {
      const team = await joinTeamByCode(teamCode);
      onJoin(team?.name || teamName);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setJoining(null);
    }
  };

  const create = async () => {
    if (!newName.trim() || !newTag.trim()) return;
    setCreating(true);
    try {
      const team = await createTeam({ name: newName.trim(), tag: newTag.trim(), role: newRole });
      onJoin(team?.name || newName.trim());
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  const tabs = createOnly
    ? [['create', t('profile.teamModal.tabCreate')]]
    : [
        ['code', t('profile.teamModal.tabCode')],
        ['browse', t('profile.teamModal.tabBrowse')],
        ...(canCreate ? [['create', t('profile.teamModal.tabCreate')]] : []),
      ];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-center justify-center p-5"
      style={{ zIndex: 300, background: 'rgba(4,4,6,.74)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)', animation: 'fedPageIn .28s var(--fed-ease) both' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[520px] overflow-y-auto rounded-[20px] border border-line-2 shadow-card-lg"
        style={{ maxHeight: '92vh', background: 'linear-gradient(165deg,var(--fed-surface-2),var(--fed-surface))', animation: 'fedRvIn .35s var(--fed-spring) both' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 82% -12%,rgba(225,18,23,.24),transparent 55%)' }} />
        <button
          onClick={onClose}
          className="absolute z-[3] w-[34px] h-[34px] rounded-[9px] border border-line bg-surface-2 text-fg-muted cursor-pointer"
          style={{ top: 14, right: 14 }}
        >
          <Ic name="x" size={15} sw={2.2} />
        </button>

        <div className="relative p-[30px]">
          <div
            className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-4 text-accent-bright"
            style={{ background: 'rgba(225,18,23,.1)', border: '1px solid var(--fed-line-accent)' }}
          >
            <Ic name="community" size={24} sw={1.8} />
          </div>
          <h2 className="font-body font-extrabold text-fg" style={{ fontSize: 26, textTransform: 'none', letterSpacing: 0 }}>
            {createOnly ? t('profile.createTeam') : t('profile.teamModal.title')}
          </h2>
          <p className="text-fg-muted mt-2.5" style={{ fontSize: 14, lineHeight: 1.6 }}>{t('profile.teamModal.sub')}</p>

          {tabs.length > 1 && (
            <div className="flex gap-1.5 p-[5px] bg-surface-2 border border-line rounded-xl" style={{ margin: '22px 0 20px' }}>
              {tabs.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  className="flex-1 h-10 border-none rounded-lg cursor-pointer font-display font-semibold uppercase transition-colors"
                  style={{
                    fontSize: 13.5, letterSpacing: '.03em',
                    background: tab === k ? 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))' : 'transparent',
                    color: tab === k ? '#fff' : 'var(--fed-fg-muted)',
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          {tabs.length <= 1 && <div style={{ height: 22 }} />}

          {tab === 'code' ? (
            <div className="flex flex-col gap-3.5">
              <Field label={t('profile.teamModal.codeLabel')}>
                <input
                  className="fed-input"
                  placeholder="STW-2026"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  style={{ fontFamily: 'var(--fed-m)', letterSpacing: '.1em' }}
                />
              </Field>
              <p className="text-fg-faint" style={{ fontSize: 12, lineHeight: 1.5, marginTop: -4 }}>{t('profile.teamModal.codeHint')}</p>
              <button
                onClick={() => join(code, code)}
                disabled={!code || !!joining}
                className="fed-btn-primary h-[52px] gap-2.5"
                style={{ fontSize: 16, opacity: !code || joining ? 0.6 : 1 }}
              >
                {joining ? <Spin /> : <Ic name="check" size={17} sw={2.4} />}
                {joining ? t('profile.teamModal.joining') : t('register.joinTeam')}
              </button>
            </div>
          ) : tab === 'browse' ? (
            <div className="flex flex-col gap-2.5 overflow-y-auto" style={{ maxHeight: 340 }}>
              {loadingTeams ? (
                <div className="flex justify-center" style={{ padding: 40 }}><Spin size={24} /></div>
              ) : teams.length > 0 ? (
                teams.map((tm) => (
                  <div
                    key={tm.id}
                    className="flex items-center gap-3 rounded-xl border border-line"
                    style={{ padding: 12, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}
                  >
                    <Ava text={tm.tag} size={42} accent />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-semibold text-fg truncate" style={{ fontSize: 14.5 }}>{tm.name}</span>
                      <span className="font-mono text-fg-muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {tm.members?.length || 0}/5 {t('common.players').toLowerCase()}
                      </span>
                    </div>
                    {tm.avg_rating ? <PremierBadge rating={tm.avg_rating} size="sm" /> : null}
                    <button
                      onClick={() => join(tm.join_code, tm.name)}
                      disabled={!!joining}
                      className="fed-btn-ghost flex-shrink-0 h-9 px-3.5 rounded-[9px]"
                      style={{ fontSize: 12.5, opacity: joining ? 0.6 : 1 }}
                    >
                      {joining === tm.join_code ? <Spin size={13} /> : t('register.joinTeam')}
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center text-fg-muted" style={{ padding: 24, fontSize: 13.5 }}>
                  {t('profile.teamModal.noTeams')}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3.5">
              <Field label={t('profile.teamModal.nameLabel')}>
                <input className="fed-input" placeholder="Steppe Wolves" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </Field>
              <Field label={t('profile.teamModal.tagLabel')}>
                <input
                  className="fed-input"
                  placeholder="STW"
                  maxLength={10}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value.toUpperCase())}
                  style={{ fontFamily: 'var(--fed-m)', letterSpacing: '.1em' }}
                />
              </Field>
              <div>
                <div className="fed-label mb-[9px]">{t('profile.teamModal.roleLabel')}</div>
                <div className="flex gap-2 flex-wrap">
                  {ROLE_OPTS.map(([k, l]) => {
                    const on = newRole === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setNewRole(k)}
                        className="h-10 px-4 rounded-[10px] cursor-pointer font-display font-semibold uppercase transition-colors"
                        style={{
                          fontSize: 13, letterSpacing: '.03em',
                          border: `1px solid ${on ? 'var(--fed-accent)' : 'var(--fed-line-2)'}`,
                          background: on ? 'var(--fed-accent)' : 'var(--fed-surface-2)',
                          color: on ? '#fff' : 'var(--fed-fg-soft)',
                        }}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={create}
                disabled={!newName.trim() || !newTag.trim() || creating}
                className="fed-btn-primary h-[52px] gap-2.5 mt-1"
                style={{ fontSize: 16, opacity: !newName.trim() || !newTag.trim() || creating ? 0.6 : 1 }}
              >
                {creating ? <Spin /> : <Ic name="plus" size={17} sw={2.4} />}
                {creating ? t('profile.teamModal.creating') : t('profile.createTeam')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Входящие заявки в команду (видит только капитан) */
function JoinRequestsPanel({ teamId }) {
  const { fetchTeamRequests, respondToJoinRequest } = useAppStore();
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const data = await fetchTeamRequests(teamId);
      if (alive) { setRequests(data || []); setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const respond = async (reqId, action) => {
    setBusy(reqId);
    try {
      await respondToJoinRequest(reqId, action);
      setRequests((rs) => rs.filter((r) => r.id !== reqId));
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex justify-center" style={{ padding: 20 }}><Spin size={20} /></div>;
  if (requests.length === 0) {
    return (
      <div className="text-center text-fg-muted" style={{ padding: '12px 0', fontSize: 13.5 }}>
        {t('profile.noRequests')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {requests.map((r) => (
        <div
          key={r.id}
          className="flex items-center gap-3 rounded-xl border border-line"
          style={{ padding: '11px 13px', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}
        >
          <Ava text={(r.user?.nickname || '?').slice(0, 2).toUpperCase()} size={38} radius={10} />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-semibold text-fg truncate" style={{ fontSize: 14 }}>{r.user?.nickname || '—'}</span>
            {r.user?.full_name && <span className="text-fg-muted truncate" style={{ fontSize: 12, marginTop: 1 }}>{r.user.full_name}</span>}
          </div>
          <button
            onClick={() => respond(r.id, 'accept')}
            disabled={busy === r.id}
            className="flex-shrink-0 h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-bold uppercase"
            style={{ fontSize: 12, letterSpacing: '.04em', border: '1px solid rgba(67,217,106,.4)', background: 'rgba(67,217,106,.1)', color: 'var(--fed-ok)' }}
          >
            {busy === r.id ? <Spin size={13} /> : t('profile.accept')}
          </button>
          <button
            onClick={() => respond(r.id, 'reject')}
            disabled={busy === r.id}
            className="fed-btn-ghost flex-shrink-0 h-9 px-3.5 rounded-[9px]"
            style={{ fontSize: 12 }}
          >
            {t('profile.decline')}
          </button>
        </div>
      ))}
    </div>
  );
}

/** Карточка команды: нет команды / заявка на рассмотрении / состав */
function TeamPanel({ u, team }) {
  const { removeTeamMember, deleteTeam, leaveTeam } = useAppStore();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [kickBusy, setKickBusy] = useState(null);
  const [confirmDissolve, setConfirmDissolve] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const td = u.team_data;
  const isCaptain = td?.captain_id === u.id;
  const members = td?.members || [];

  const copyCode = () => {
    navigator.clipboard?.writeText(td?.join_code || '').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const kick = async (userId) => {
    setKickBusy(userId);
    try { await removeTeamMember(td.id, userId); } catch (e) {}
    setKickBusy(null);
  };

  const dissolve = async () => {
    if (!confirmDissolve) { setConfirmDissolve(true); return; }
    setActionBusy(true);
    try { await deleteTeam(td.id); } catch (e) {}
    setActionBusy(false); setConfirmDissolve(false);
  };

  const leave = async () => {
    if (!confirmLeave) { setConfirmLeave(true); return; }
    setActionBusy(true);
    try { await leaveTeam(td.id); } catch (e) {}
    setActionBusy(false); setConfirmLeave(false);
  };

  const head = (
    <h3 style={{ fontSize: 20 }}>{t('profile.myTeam')}</h3>
  );

  // === заявка в команду отправлена, ждём ответа капитана ===
  if (!team && u.pending_request) {
    return (
      <Card>
        <div style={{ marginBottom: 16 }}>{head}</div>
        <div
          className="flex gap-3 items-center rounded-[13px]"
          style={{ padding: 16, background: 'rgba(59,165,217,.07)', border: '1px solid rgba(59,165,217,.3)' }}
        >
          <span className="flex-shrink-0" style={{ color: 'var(--fed-info)' }}><Ic name="info" size={22} /></span>
          <div>
            <div className="font-semibold text-fg" style={{ fontSize: 14.5 }}>{t('profile.pendingRequest')}</div>
            <div className="text-fg-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {t('profile.pendingRequestSub')}{' '}
              <strong className="text-fg">«{u.pending_request.team?.name || '—'}»</strong>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // === команды нет ===
  if (!team) {
    return (
      <Card>
        <div style={{ marginBottom: 16 }}>{head}</div>
        <div
          className="flex gap-3 items-center rounded-[13px]"
          style={{ padding: 16, background: 'rgba(230,183,60,.07)', border: '1px solid rgba(230,183,60,.3)' }}
        >
          <span className="flex-shrink-0 text-gold"><Ic name="info" size={22} /></span>
          <div>
            <div className="font-semibold text-fg" style={{ fontSize: 14.5 }}>{t('profile.noTeam')}</div>
            <div className="text-fg-muted" style={{ fontSize: 13, marginTop: 2 }}>{t('profile.noTeamSub')}</div>
          </div>
        </div>
      </Card>
    );
  }

  // === команда есть ===
  const teamStatus = td?.status === 'full'
    ? { l: t('profile.fullTeam'), c: 'var(--fed-accent-bright)' }
    : td?.status === 'moderation'
      ? { l: t('profile.teamOnModeration'), c: 'var(--fed-info)' }
      : { l: t('profile.recruiting'), c: 'var(--fed-ok)' };

  return (
    <Card style={{ padding: 0 }} className="overflow-hidden">
      {/* шапка команды */}
      <div className="relative overflow-hidden" style={{ padding: 26, borderBottom: '1px solid var(--fed-line)' }}>
        <div className="absolute inset-0" style={{ opacity: 0.5, background: 'radial-gradient(ellipse at 92% -30%,rgba(225,18,23,.22),transparent 60%)' }} />
        <div className="relative flex justify-between items-center flex-wrap gap-2" style={{ marginBottom: 18 }}>
          {head}
          <div className="flex gap-2 flex-wrap">
            <Pill color={isCaptain ? 'var(--fed-gold)' : 'var(--fed-ok)'}>
              {isCaptain ? t('common.captain') : t('profile.inTeam')}
            </Pill>
            <Pill color={teamStatus.c} dot>{teamStatus.l}</Pill>
          </div>
        </div>
        <div className="relative flex gap-4 items-center">
          <Ava text={(td?.tag || team).slice(0, 3).toUpperCase()} size={58} radius={14} accent glowColor="var(--fed-accent-glow)" />
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className="font-display font-bold text-fg uppercase" style={{ fontSize: 22, lineHeight: 1.05 }}>{team}</span>
            {isCaptain && td?.join_code && (
              <div className="flex gap-2 items-center flex-wrap">
                <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.12em' }}>
                  {t('profile.joinCode')}
                </span>
                <span className="font-mono font-bold text-gold" style={{ fontSize: 13, letterSpacing: '.14em' }}>{td.join_code}</span>
                <button
                  onClick={copyCode}
                  className="cursor-pointer font-mono rounded-md transition-colors"
                  style={{
                    padding: '3px 9px', fontSize: 10.5, letterSpacing: '.06em',
                    border: `1px solid ${copied ? 'rgba(67,217,106,.45)' : 'var(--fed-line-2)'}`,
                    background: copied ? 'rgba(67,217,106,.1)' : 'var(--fed-surface-2)',
                    color: copied ? 'var(--fed-ok)' : 'var(--fed-fg-muted)',
                  }}
                >
                  {copied ? t('profile.codeCopied') : t('profile.copyCode')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* состав */}
      <div style={{ padding: 26 }}>
        <BlockLabel style={{ marginBottom: 12 }}>{t('profile.roster')} · {members.length}/5</BlockLabel>
        {members.length === 0 ? (
          <div className="text-fg-faint" style={{ fontSize: 13, padding: '8px 0' }}>{t('profile.noMembers')}</div>
        ) : (
          <div className="flex flex-col gap-2">
            {members.map((m) => {
              const isMe = m.user_id === u.id;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-3 rounded-xl"
                  style={{
                    padding: '10px 12px',
                    background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))',
                    border: `1px solid ${isMe ? 'var(--fed-line-accent)' : 'var(--fed-line)'}`,
                  }}
                >
                  <Ava text={(m.user?.nickname || '?').slice(0, 2).toUpperCase()} size={36} radius={10} accent={isMe} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="font-semibold text-fg truncate" style={{ fontSize: 14 }}>{m.user?.nickname || '—'}</span>
                    {m.user?.full_name && <span className="text-fg-muted truncate" style={{ fontSize: 11.5, marginTop: 1 }}>{m.user.full_name}</span>}
                  </div>
                  {td?.captain_id === m.user_id && (
                    <span className="flex-shrink-0 font-mono uppercase text-gold" style={{ fontSize: 9, letterSpacing: '.1em' }}>
                      {t('common.captain')}
                    </span>
                  )}
                  {isMe && (
                    <span className="flex-shrink-0 font-mono uppercase" style={{ fontSize: 9, letterSpacing: '.1em', color: 'var(--fed-info)' }}>
                      {t('profile.youLabel')}
                    </span>
                  )}
                  <span
                    className="flex-shrink-0 font-mono uppercase rounded-full border border-line bg-surface-2 text-fg-muted"
                    style={{ fontSize: 9.5, letterSpacing: '.08em', padding: '3px 9px' }}
                  >
                    {ROLE_LABEL[m.role] || m.role || '—'}
                  </span>
                  {isCaptain && !isMe && (
                    <button
                      onClick={() => kick(m.user_id)}
                      disabled={kickBusy === m.user_id}
                      title={t('profile.kickPlayer')}
                      className="fed-iconbtn"
                      style={{ width: 28, height: 28, borderRadius: 8 }}
                    >
                      {kickBusy === m.user_id ? <Spin size={12} /> : <Ic name="x" size={12} sw={2.4} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* заявки в команду */}
        {isCaptain && (
          <div style={{ marginTop: 22 }}>
            <BlockLabel color="var(--fed-gold)" style={{ marginBottom: 12 }}>{t('profile.joinRequests')}</BlockLabel>
            <JoinRequestsPanel teamId={td.id} />
          </div>
        )}

        {/* распустить / выйти */}
        <div className="flex gap-2.5 items-center flex-wrap" style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--fed-line)' }}>
          <button
            onClick={isCaptain ? dissolve : leave}
            disabled={actionBusy}
            className="h-10 px-4 gap-2 rounded-[10px] cursor-pointer font-display font-bold uppercase transition-colors"
            style={{
              fontSize: 12.5, letterSpacing: '.04em',
              border: `1px solid ${(isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'var(--fed-accent)' : 'rgba(230,183,60,.55)') : 'var(--fed-line-2)'}`,
              background: (isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'rgba(225,18,23,.1)' : 'rgba(230,183,60,.08)') : 'transparent',
              color: (isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'var(--fed-accent-bright)' : 'var(--fed-gold)') : 'var(--fed-fg-muted)',
            }}
          >
            {actionBusy ? <Spin size={14} /> : <Ic name={isCaptain ? 'trash' : 'logout'} size={14} sw={2} />}
            {isCaptain
              ? (confirmDissolve ? t('profile.confirmDissolve') : t('profile.dissolveTeam'))
              : (confirmLeave ? t('profile.confirmLeave') : t('profile.leaveTeam'))}
          </button>
          {(isCaptain ? confirmDissolve : confirmLeave) && (
            <button
              onClick={() => (isCaptain ? setConfirmDissolve(false) : setConfirmLeave(false))}
              className="fed-btn-ghost h-10 px-4 rounded-[10px]"
              style={{ fontSize: 12.5 }}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function FedProfile() {
  const { go, user, openTeamModal, setOpenTeamModal, fetchMe } = useAppStore();
  const { t } = useTranslation();

  useEffect(() => { fetchMe(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const u = user || {};
  const userData = {
    nick: u.nickname || u.nick || 'newcomer',
    name: u.full_name || u.name || 'Игрок',
    rating: u.premier_rating || u.rating || 0,
    status: u.status || 'none',
    role: u.role || '—',
    doc: !!u.document_file || u.doc || false,
    edu: u.education_name ? `${u.education_name} · ${u.grade || ''}` : u.edu || '',
    team: u.team || null,
    id: u.id || 'me',
  };

  const tier = premierTier(userData.rating || 0);
  const [team, setTeam] = useState(userData.team || null);
  // в профиле оставлено только создание команды — поиск/вступление перенесены на страницу турнира
  const openCreate = () => setOpenTeamModal(true);

  useEffect(() => { setTeam(userData.team || null); }, [userData.team]);

  // === не авторизован ===
  if (!user) {
    return (
      <div className="fed-pagewrap max-w-[1320px] mx-auto text-center" style={{ padding: '80px 24px 96px' }}>
        <Rv>
          <h1 style={{ fontSize: 'clamp(30px,5vw,48px)' }}>{t('profile.loginRequired')}</h1>
          <p className="text-fg-soft mx-auto" style={{ fontSize: 15.5, lineHeight: 1.6, maxWidth: 440, marginTop: 14 }}>
            {t('profile.loginRequiredSub')}
          </p>
          <div className="flex gap-3 justify-center flex-wrap" style={{ marginTop: 28 }}>
            <button onClick={() => go('auth')} className="fed-btn-primary h-[52px] px-6 gap-2" style={{ fontSize: 16 }}>
              <Ic name="user" size={17} sw={2} />
              {t('nav.login')} / {t('nav.signup')}
            </button>
            <button onClick={() => go('home')} className="fed-btn-ghost h-[52px] px-[22px]" style={{ fontSize: 16 }}>
              {t('nav.home')}
            </button>
          </div>
        </Rv>
      </div>
    );
  }

  const statusLabel = userData.status === 'verified'
    ? t('profile.chipVerified')
    : userData.status === 'rejected'
      ? t('profile.chipRejected')
      : userData.status === 'none' ? t('profile.chipNone') : t('profile.chipModeration');
  const tone = toneOf(userData.status === 'submitted' || userData.status === 'moderation' ? 'pending' : userData.status);

  return (
    <div className="fed-pagewrap max-w-[1320px] mx-auto" style={{ padding: '28px 24px 72px' }}>
      {/* ===== шапка игрока ===== */}
      <Rv className="relative overflow-hidden rounded-[20px] border border-line-2 shadow-card" style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', marginBottom: 22 }}>
        <div className="absolute inset-0" style={{ opacity: 0.55, background: 'radial-gradient(ellipse at 88% -20%,rgba(225,18,23,.28),transparent 58%)' }} />
        <div className="relative flex gap-5 items-center flex-wrap p-[30px] max-[560px]:p-5 max-[560px]:gap-4">
          <div
            className="flex-shrink-0 flex items-center justify-center font-display font-bold text-fg"
            style={{
              width: 104, height: 104, borderRadius: 20, fontSize: 38,
              background: `linear-gradient(140deg,${tier.c}33,var(--fed-surface-3))`,
              border: `2px solid ${tier.c}`,
              boxShadow: `0 0 34px -8px ${tier.c}88`,
            }}
          >
            {initials(userData.name)}
          </div>

          <div className="flex flex-col gap-2.5 flex-1" style={{ minWidth: 200 }}>
            <div className="flex gap-3 items-center flex-wrap">
              <h1 style={{ fontSize: 'clamp(28px,4vw,44px)' }}>{userData.nick}</h1>
              {userData.rating > 0 && <PremierBadge rating={userData.rating} />}
            </div>
            <div className="text-fg-soft" style={{ fontSize: 15 }}>{userData.name}</div>
            <div className="flex gap-2 flex-wrap" style={{ marginTop: 2 }}>
              <Pill color={tone.c} bg={tone.bg} border={tone.bd} dot>{statusLabel}</Pill>
              {userData.edu && <Pill>{userData.edu}</Pill>}
            </div>
          </div>

          <div className="flex flex-col gap-2.5 flex-shrink-0 max-[560px]:w-full empty:hidden">
            {!team ? (
              userData.status === 'verified' && (
                <button onClick={openCreate} className="fed-btn-primary h-11 px-5 gap-2 rounded-[11px] max-[560px]:w-full" style={{ fontSize: 14.5 }}>
                  <Ic name="plus" size={16} sw={2.2} />
                  {t('profile.createTeam')}
                </button>
              )
            ) : (
              <button onClick={() => go('tournaments')} className="fed-btn-ghost h-11 px-5 gap-2 rounded-[11px] max-[560px]:w-full" style={{ fontSize: 14.5 }}>
                <Ic name="trophy" size={16} sw={2} />
                {t('profile.bracketBtn')}
              </button>
            )}
          </div>
        </div>
      </Rv>

      {/* ===== команда + игровой профиль ===== */}
      <Rv className="flex flex-col gap-5">
        <TeamPanel u={u} team={team} />

        <Card>
          <h3 style={{ fontSize: 20, marginBottom: 16 }}>{t('profile.gameProfile')}</h3>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))' }}>
            <MetaTile
              l={t('profile.premier')}
              v={userData.rating > 0 ? userData.rating.toLocaleString('ru-RU') : '—'}
              color={userData.rating > 0 ? tier.c : 'var(--fed-fg)'}
            />
            <MetaTile l={t('profile.role')} v={userData.role !== '—' ? userData.role : '—'} />
            <MetaTile
              l={t('profile.docLabel')}
              v={userData.doc ? t('profile.docUploaded') : t('profile.docMissing')}
              color={userData.doc ? 'var(--fed-ok)' : 'var(--fed-gold)'}
            />
          </div>
        </Card>
      </Rv>

      <TeamJoinModal
        open={openTeamModal}
        createOnly
        onClose={() => setOpenTeamModal(false)}
        onJoin={(name) => { setTeam(name); setOpenTeamModal(false); }}
      />
    </div>
  );
}
